import type { CodeProfile } from "./types";
import type { EcOptions } from "../types/input";

/**
 * EN 1997-1 Ek A kısmi katsayı setleri (önerilen değerler) ve ulusal ek farkları.
 * A1/A2: etkiler (γG unfavourable/favourable, γQ) · M1/M2: malzeme (γφ tanφ üzerinde, γc) · R1/R2/R3: dirençler (kayma γRh, taşıma γRv, pasif γRe).
 * EQU: γG,dst 1,1 · γG,stb 0,9 · γQ 1,5 · M2.
 */
export interface PartialFactors { gG: number; gGfav: number; gQ: number; gPhi: number; gC: number; gRh: number; gRv: number; gRe: number }
export interface EcCombination { id: string; label: string; f: PartialFactors; limitState: "GEO" | "EQU" }

const A1 = { gG: 1.35, gGfav: 1.0, gQ: 1.5 }, A2 = { gG: 1.0, gGfav: 1.0, gQ: 1.3 };
const M1 = { gPhi: 1.0, gC: 1.0 }, M2 = { gPhi: 1.25, gC: 1.25 };
const R1 = { gRh: 1.0, gRv: 1.0, gRe: 1.0 }, R2 = { gRh: 1.1, gRv: 1.4, gRe: 1.4 }, R3 = { gRh: 1.0, gRv: 1.0, gRe: 1.0 };

/** Ulusal ek farkları: UK (BS EN 1997-1 NA) DA1 kullanır, EQU'da γG,dst 1,1/γG,stb 0,9 aynı; DE (DIN 1054) DA2* (etkiler sonuçlara uygulanır) → burada DA2 ile temsil. */
export function ecCombinations(opt: EcOptions): EcCombination[] {
  const equ: EcCombination = { id: "EQU", label: "EQU (devrilme)", limitState: "EQU", f: { gG: 1.1, gGfav: 0.9, gQ: 1.5, ...M2, ...R1 } };
  if (opt.approach === "DA2") return [equ, { id: "DA2", label: "DA2 (A1+M1+R2)", limitState: "GEO", f: { ...A1, ...M1, ...R2 } }];
  if (opt.approach === "DA3") return [equ, { id: "DA3", label: "DA3 (A2*+M2+R3)", limitState: "GEO", f: { ...A2, ...M2, ...R3 } }];
  return [
    equ,
    { id: "DA1-C1", label: "DA1 Kombinasyon 1 (A1+M1+R1)", limitState: "GEO", f: { ...A1, ...M1, ...R1 } },
    { id: "DA1-C2", label: "DA1 Kombinasyon 2 (A2+M2+R1)", limitState: "GEO", f: { ...A2, ...M2, ...R1 } },
  ];
}

/** Depremli tasarım durumu (EN 1990 6.4.3.4 + EN 1998-5 §3.1): γG = γQ = 1,0 (ψ2·Q), malzeme M2, direnç R1. */
export function ecSeismicCombination(opt: EcOptions): EcCombination {
  return { id: "SEIS", label: "Depremli (G + ψ2·Q + AE, M2)", limitState: "GEO", f: { gG: 1.0, gGfav: 1.0, gQ: opt.psi2, ...M2, ...R1 } };
}

export const EC_PROFILE: CodeProfile = {
  id: "ec7",
  label: "EC7",
  verification: "partial",
  references: [
    "EN 1997-1:2004 Eurocode 7 — Geoteknik tasarım, Bölüm 1: Genel kurallar; Ek A (kısmi katsayılar), Ek D (taşıma gücü).",
    "EN 1998-5:2004 Eurocode 8 — Bölüm 5: Temeller, istinat yapıları ve geoteknik konular (§7.3.2, Ek E).",
    "EN 1990:2002 — Yapı tasarımının esasları (kombinasyonlar, ψ katsayıları).",
  ],
};
